import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import { ContextType } from '@lit/context';
import { live } from 'lit/directives/live.js';
import { pageMetadata } from '@flakiness/server/common/pageMetadata.js';
import { api } from './api.js';
import { docLinkStyles, pageStyles } from './components/cssstyles.js';
import { OrgAlerts } from './components/org-alerts.js';
import { OrganizationContext } from './components/organization-context.js';
import { BillingEmailDetail, PickBillingEmail } from './components/pick-billing-email.js';
import { OrgSlugPicked, PickOrgName } from './components/pick-org-name.js';
import { consume, contexts, FKOrgChanged, TaskEvent } from './contexts.js';
import { githubMarkdownCSS } from './githubMarkdownCSS.js';
import { PageUserOrganizations } from './page-user-organizations.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

@customElement('page-organization-settings')
export class PageOrganizationSettings extends LitElement {

  static url(options: { orgSlug: string }): string {
    return platformUrl(['', options.orgSlug, 'settings'].join('/'));
  }

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/:org/settings',
      ],
      render: (groups) => OrganizationContext.render({
        orgSlug: groups?.org,
      }, html`
        <page-organization-settings
          .orgSlug=${groups?.org}
        ></page-organization-settings>
      `),
    }];
  }

  @consume({ context: contexts.router, subscribe: true }) private _router?: ContextType<typeof contexts.router>;
  @consume({ context: contexts.user, subscribe: true }) private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.org, subscribe: true }) private _org?: ContextType<typeof contexts.org>;
  @consume({ context: contexts.serverInfo, subscribe: true }) private _serverInfo?: ContextType<typeof contexts.serverInfo>;

  @property({ attribute: false }) orgSlug?: string;
  
  @query('pick-org-name') private _orgSlugInput?: PickOrgName;
  @query('pick-billing-email') private _billingEmailInput?: PickBillingEmail;

  @state() private _orgRename?: OrgSlugPicked;
  @state() private _billingEmailDraft?: BillingEmailDetail;

  private async _renameOrg() {
    const newOrgSlug = this._orgRename?.orgSlug;
    const orgSlug = this.orgSlug;
    if (!newOrgSlug || !orgSlug)
      return;
    await api.organization.setOrganizationSlug.POST({
      orgSlug,
      newOrgSlug,
    });
    this._orgRename = undefined;
    this._orgSlugInput?.setStatus(true, 'Organization name updated');
    await this._router?.navigate(PageOrganizationSettings.url({
      orgSlug: newOrgSlug,
    }));
  }

  private _canRenameOrg(): boolean {
    const currentOrgSlug = this.orgSlug;
    const newOrgSlug = this._orgSlugInput?.value();
    return !!currentOrgSlug && !!newOrgSlug && newOrgSlug !== currentOrgSlug && !!this._orgSlugInput?.success();
  }

  private _canSaveBillingEmail(): boolean {
    if (!this.orgSlug || !this._billingEmailDraft?.valid)
      return false;
    return this._billingEmailDraft.value !== (this._org?.billingEmail ?? '');
  }

  private async _saveBillingEmail() {
    const orgSlug = this.orgSlug;
    const draft = this._billingEmailDraft?.value;
    if (!orgSlug || !draft)
      return;
    try {
      await api.organization.setOrganizationBillingEmail.POST({
        orgSlug,
        billingEmail: draft,
      });
      // Org context is refresh-on-event (project-context only reruns its org
      // task on fk-org-changed). Without dispatching, _org.billingEmail stays
      // at the old value and the input would snap back to it after reset().
      this.dispatchEvent(new FKOrgChanged({ orgSlug }));
      this._billingEmailInput?.setStatus(true, 'Billing email updated');
    } catch (e) {
      this._billingEmailInput?.setStatus(false, e instanceof Error ? e.message : 'Failed to update billing email');
    }
  }

  private async _deleteOrg() {
    const { orgSlug } = this;
    if (!orgSlug || !this._router)
      return;
    const value = prompt(`Type in "${orgSlug}" to confirm deletion`)
    if (value?.trim() !== orgSlug)
      return;
    await api.organization.delete.POST({ orgSlug });
    this._router.navigate(PageUserOrganizations.url());
  }

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [this.orgSlug] as const,
    metadata: ([orgSlug]) => orgSlug ? pageMetadata({ orgSlug, section: 'Settings', accessGated: true }) : undefined,
  });

  render() {
    if (this._org && !this._org.access)
      return html`<page-http-error code=403></page-http-error>`;

    return html`
      <org-header .orgSlug=${this.orgSlug} submenu=settings></org-header>
      <app-body user-role=${this._org?.access}>
        ${OrgAlerts.maybeRender(this._org)}
        <h1>Organization Settings</h1>

        <form-section label="Organization name">
          <pick-org-name
            ?disabled=${!this._org?.access || this._org.access === 'member'}
            .initial=${live(this._org?.orgSlug)}
            @org-slug=${(event: TaskEvent<OrgSlugPicked>) => this._orgRename = event.data }
          >
            <sl-button ?disabled=${!this._canRenameOrg()} @click=${() => this._renameOrg()}>Update Name</sl-button>
          </pick-org-name>
        </form-section>
        <sl-divider></sl-divider>

        ${this._org?.access === 'admin' || this._org?.access === 'owner' ? html`
          <pick-billing-email
            .initial=${this._org?.billingEmail ?? ''}
            @billing-email=${(e: CustomEvent<BillingEmailDetail>) => this._billingEmailDraft = e.detail}
          >
            <sl-button ?disabled=${!this._canSaveBillingEmail()} @click=${() => this._saveBillingEmail()}>Update Email</sl-button>
          </pick-billing-email>
          <sl-divider></sl-divider>
        ` : nothing}

        <form-section is-editor>
          <sl-button ?disabled=${this._org?.access !== 'owner' && !this._user?.isSuperUser} @click=${this._deleteOrg.bind(this)} variant=danger>Delete Organization</sl-button>
        </form-section>

      </app-body>
      <app-footer></app-footer>
    `
  }

  static styles = [githubMarkdownCSS, docLinkStyles, pageStyles, css`

    [user-role=member] *:is([is-admin], [is-owner]) {
      display: none;
    }

    [user-role=admin] *:is([is-owner]) {
      display: none;
    }
  `];
}
